import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, EnvelopeSimple, Phone, Bell, ChatCircleDots, FloppyDisk, ShieldCheck, Palette, Camera, Clock } from "@phosphor-icons/react";
import COLOR_THEMES, { applyTheme } from "@/lib/themes";
import { TIMEZONE_OPTIONS, BROWSER_TZ } from "@/lib/timezone";
import SmsEventPreferences from "@/pages/SmsEventPreferences";
import { resolveBackendFileUrl } from "@/lib/fileUrls";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function resolveFileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/api/")) return `${BACKEND_URL}${path}`;
  return path;
}

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", receive_email_alerts: true, receive_sms_alerts: false, color_theme: "default", timezone: BROWSER_TZ });
  const fileRef = useRef(null);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get("/auth/me");
      setProfile(res.data);
      setForm({
        name: res.data.name || "",
        phone: res.data.phone || "",
        receive_email_alerts: res.data.receive_email_alerts !== false,
        receive_sms_alerts: !!res.data.receive_sms_alerts,
        color_theme: res.data.color_theme || "default",
        timezone: res.data.timezone || BROWSER_TZ,
      });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please select an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/auth/upload-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newAvatar = res.data.avatar;
      setProfile(p => ({ ...p, avatar: newAvatar }));
      if (setUser) setUser(prev => ({ ...prev, avatar: newAvatar }));
      toast.success("Profile picture updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      setSaving(true);
      const res = await api.put("/auth/profile", form);
      setProfile(res.data);
      if (setUser) setUser(prev => {
        const next = { ...prev, name: form.name, phone: form.phone, color_theme: form.color_theme, timezone: form.timezone };
        // Mirror the profile change into localStorage so non-React
        // utilities (e.g. lib/timezone.userTz) pick up the new tz on
        // the very next render without waiting for a full reload.
        try { localStorage.setItem("eyvo_user", JSON.stringify(next)); } catch {}
        return next;
      });
      applyTheme(form.color_theme);
      toast.success("Profile updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  return (
    <div className="page-enter p-6 sm:p-8 max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>My Profile</h1>
        <p className="text-sm text-[#525252] mt-1">Manage your account settings and notification preferences</p>
      </div>

      {/* Account Info */}
      <Card className="border border-[#E5E5E5] rounded-none p-6 space-y-5">
        <div className="flex items-center gap-4 pb-4 border-b border-[#E5E5E5]">
          <div className="relative group">
            <div className="w-16 h-16 bg-[#F0F7FF] flex items-center justify-center overflow-hidden rounded-full">
              {profile?.avatar ? (
                <img src={resolveBackendFileUrl(profile.avatar)} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={28} className="text-[#0055FF]" weight="duotone" />
              )}
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all cursor-pointer"
              data-testid="avatar-upload-trigger"
            >
              <Camera size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" weight="bold" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-file-input" />
            {uploading && <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center"><div className="w-4 h-4 border-2 border-[#0055FF] border-t-transparent animate-spin rounded-full" /></div>}
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0A0A0A]">{profile?.name || "User"}</h2>
            <p className="text-xs text-[#A3A3A3]">{profile?.email}</p>
            <button onClick={() => fileRef.current?.click()} className="text-[10px] text-[#0055FF] hover:text-[#003ECB] font-bold uppercase tracking-wider mt-1" data-testid="change-photo-link">
              {profile?.avatar ? "Change Photo" : "Upload Photo"}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Full Name</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" data-testid="profile-name-input" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Email Address</Label>
            <Input value={profile?.email || ""} disabled className="rounded-none border-[#E5E5E5] h-10 bg-[#FAFAFA] text-[#A3A3A3]" />
            <p className="text-[10px] text-[#A3A3A3]">Email cannot be changed. Contact your administrator if needed.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Phone Number</Label>
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" placeholder="+1 (555) 000-0000" data-testid="profile-phone-input" />
          </div>
        </div>
      </Card>

      {/* Notification Preferences */}
      <Card className="border border-[#E5E5E5] rounded-none p-6 space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E5E5E5]">
          <Bell size={18} className="text-[#0055FF]" />
          <h2 className="text-lg font-bold text-[#0A0A0A]">Notification Preferences</h2>
        </div>

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors" data-testid="email-alerts-toggle">
          <input type="checkbox" checked={form.receive_email_alerts} onChange={e => setForm({...form, receive_email_alerts: e.target.checked})} className="mt-0.5 h-4 w-4 rounded-none border-[#D4D4D4] text-[#0055FF] focus:ring-[#0055FF]" />
          <div>
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={16} className="text-[#525252]" />
              <span className="text-sm font-medium text-[#0A0A0A]">Email Notifications</span>
            </div>
            <p className="text-xs text-[#A3A3A3] mt-0.5">Receive email alerts for project updates, messages, and change request status changes.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors" data-testid="sms-alerts-toggle">
          <input type="checkbox" checked={form.receive_sms_alerts} onChange={e => setForm({...form, receive_sms_alerts: e.target.checked})} className="mt-0.5 h-4 w-4 rounded-none border-[#D4D4D4] text-[#0055FF] focus:ring-[#0055FF]" />
          <div>
            <div className="flex items-center gap-2">
              <ChatCircleDots size={16} className="text-[#525252]" />
              <span className="text-sm font-medium text-[#0A0A0A]">SMS Notifications (master switch)</span>
            </div>
            <p className="text-xs text-[#A3A3A3] mt-0.5">Required to receive ANY text messages. Individual event-type preferences are below. Message &amp; data rates may apply. Reply STOP to unsubscribe.</p>
            {form.receive_sms_alerts && !form.phone && (
              <p className="text-xs text-[#DC2626] mt-1 font-medium">Please enter your phone number above to receive SMS alerts.</p>
            )}
          </div>
        </label>
      </Card>

      <SmsEventPreferences masterOn={form.receive_sms_alerts && !!form.phone} />

      {/* Security */}
      <Card className="border border-[#E5E5E5] rounded-none p-6">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E5E5E5] mb-4">
          <ShieldCheck size={18} className="text-[#16A34A]" />
          <h2 className="text-lg font-bold text-[#0A0A0A]">Security</h2>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[#0A0A0A]">Two-Factor Authentication</p>
            <p className="text-xs text-[#A3A3A3]">Your account is protected with TOTP 2FA</p>
          </div>
          <span className="text-xs font-bold uppercase text-[#16A34A] bg-[#F0FDF4] px-3 py-1">{profile?.totp_enabled ? "Enabled" : "Not Set Up"}</span>
        </div>
      </Card>

      {/* Timezone */}
      <Card className="border border-[#E5E5E5] rounded-none p-6" data-testid="timezone-card">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E5E5E5] mb-4">
          <Clock size={18} style={{ color: "var(--theme-primary)" }} />
          <h2 className="text-lg font-bold text-[#0A0A0A]">Time Zone</h2>
        </div>
        <p className="text-xs text-[#525252] mb-3">All dates and times in the app — proposal opens, audit trail, ticket activity, emails — are displayed in this zone.</p>
        <div className="space-y-2">
          <Label htmlFor="tz">Your time zone</Label>
          <select
            id="tz"
            value={form.timezone || ""}
            onChange={e => setForm({ ...form, timezone: e.target.value })}
            className="w-full h-10 border border-[#E5E5E5] rounded-none px-3 text-sm focus:outline-none focus:border-[#0055FF] bg-white"
            data-testid="timezone-select"
          >
            {TIMEZONE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
            {/* If the stored tz isn't in the curated list, show it as-is so it's never lost */}
            {form.timezone && !TIMEZONE_OPTIONS.some(o => o.value === form.timezone) && (
              <option value={form.timezone}>{form.timezone}</option>
            )}
          </select>
          <p className="text-[11px] text-[#A3A3A3]">Browser-detected: <span className="font-mono">{BROWSER_TZ}</span></p>
        </div>
      </Card>

      {/* Color Theme */}
      <Card className="border border-[#E5E5E5] rounded-none p-6" data-testid="theme-picker-card">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E5E5E5] mb-4">
          <Palette size={18} style={{ color: "var(--theme-primary)" }} />
          <h2 className="text-lg font-bold text-[#0A0A0A]">Color Theme</h2>
        </div>
        <p className="text-xs text-[#A3A3A3] mb-4">Choose a theme. Controls navigation, page background, text colors, and field styling.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          {Object.entries(COLOR_THEMES).map(([key, theme]) => (
            <button
              key={key}
              onClick={() => { setForm({ ...form, color_theme: key }); applyTheme(key); }}
              className={`flex flex-col items-center gap-2 p-3 border-2 transition-all ${
                form.color_theme === key ? "border-[#0A0A0A] shadow-sm" : "border-[#E5E5E5] hover:border-[#A3A3A3]"
              }`}
              data-testid={`theme-${key}`}
            >
              <div className="w-full h-6 flex overflow-hidden border border-[#E5E5E5]/50">
                <div className="w-1/4" style={{ backgroundColor: theme.sidebar }} />
                <div className="flex-1 relative" style={{ backgroundColor: theme.pageBg }}>
                  <div className="absolute top-1 left-1 w-3 h-1" style={{ backgroundColor: theme.color }} />
                  <div className="absolute top-3 left-1 right-1 h-0.5" style={{ backgroundColor: theme.fieldBorder }} />
                </div>
              </div>
              <span className="text-[10px] font-medium text-[#0A0A0A]">{theme.label}</span>
            </button>
          ))}
        </div>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="save-profile-button">
        <FloppyDisk size={16} className="mr-2" />
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );
}


